import React, { useCallback, useState } from 'react';
import { parseBuffer } from 'music-metadata-browser';
import { runWorker } from '../worker-messages';

export interface LoadedAudio {
  file: File;
  title: string;
  artist: string;
  durationMs: number;
  hash: string;
}

interface AudioLoaderProps {
  onLoaded?: (info: LoadedAudio) => void;
  onLog?: (msg: string) => void;
}

export default function AudioLoader({ onLoaded, onLog }: AudioLoaderProps) {
  const [info, setInfo] = useState<LoadedAudio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setInfo(null);
      onLog?.(`Loading file: ${file.name}`);

      const supportedMimes = ['audio/mpeg', 'audio/ogg', 'audio/wav'];
      const supportedExts = ['mp3', 'ogg', 'wav'];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (
        !supportedMimes.includes(file.type) &&
        (!ext || !supportedExts.includes(ext))
      ) {
        setError('Unsupported audio format. Please use MP3, OGG, or WAV.');
        onLog?.(`Unsupported format: ${file.type || ext}`);
        return;
      }

      try {
        onLog?.('Reading file into memory...');
        const arrayBuffer = await file.arrayBuffer();

        let title = file.name;
        let artist = '';
        try {
          onLog?.('Parsing metadata...');
          const metadata = await parseBuffer(new Uint8Array(arrayBuffer), file.type);
          title = metadata.common.title ?? file.name;
          artist = metadata.common.artist ?? '';
        } catch (err) {
          const message = (err as Error).message;
          onLog?.(`Failed to parse metadata: ${message}`);
        }

        onLog?.('Decoding audio data...');
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
        const durationMs = audioBuffer.duration * 1000;
        await audioCtx.close();
        onLog?.(`Decoded audio. Duration: ${(durationMs / 1000).toFixed(2)}s`);

        let hash: string;
        try {
          onLog?.('Computing hash in worker...');
          ({ hash } = await runWorker({ type: 'hash', buffer: arrayBuffer }, onLog));
        } catch {
          onLog?.('Hash worker failed, falling back to main thread');
          // Fall back to hashing on the main thread if worker fails
          const digest = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(digest));
          hash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        }
        onLog?.(`Hash computed: ${hash}`);
        const info = { file, title, artist, durationMs, hash };
        setInfo(info);
        onLoaded?.(info);
        onLog?.('Audio loaded successfully');
      } catch (err) {
        const message = (err as Error).message;
        setError(message);
        onLog?.(`Error loading audio: ${message}`);
      }
    },
    [onLoaded, onLog]
  );

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFile(file);
    }
  }, [handleFile]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleFile(file);
    }
  }, [handleFile]);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  return (
    <div className="p-4">
      <div
        className="flex h-40 items-center justify-center rounded border border-dashed"
        onDrop={onDrop}
        onDragOver={onDragOver}
      >
        <span>Drop MP3, OGG, or WAV here or use the file input below.</span>
      </div>
      <input
        type="file"
        accept="audio/mpeg,audio/ogg,audio/wav"
        onChange={onInputChange}
        className="mt-2 bg-white text-black"
      />
      {info && (
        <div className="mt-2">
          <div>Title: {info.title}</div>
          <div>Artist: {info.artist}</div>
          <div>Duration: {(info.durationMs / 1000).toFixed(2)}s</div>
          <div>SHA-256: {info.hash}</div>
        </div>
      )}
      {error && <div className="mt-2 text-red-500">{error}</div>}
    </div>
  );
}
